import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { OrderedMap, Map, Set } from 'immutable';
import VisibilitySensor from 'react-visibility-sensor';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import moment from 'moment-timezone';

import AdditionalInformation from 'containers/checkout/additional-information';

import ModalNotices from 'components/checkout/modal-notices';
import CheckoutForm from 'components/checkout/checkout-form';
import Headline from 'components/checkout/headline';
import SubHeadline from 'components/checkout/sub-headline';
import VenueSubheader from 'components/checkout/venue-subheader';
import MobileHeader from 'components/checkout/mobile-header';
import EventTooltip from 'components/checkout/event-tooltip';

import User from 'models/user';
import Search from 'models/search';
import Pricing from 'models/pricing';
import PaymentMethod from 'models/payment-method';
import Venue from 'models/venue';
import Hub from 'models/hub';
import Event from 'models/event';
import { AddOns as AddOnsModel } from 'models/addons';
import { Location } from 'models/locations';
import Quote from 'models/quote';

import { routerLocationShape } from 'lib/common/prop-types';
import { pageProps, urlProps } from 'lib/analytics/page-properties';
import * as AppContext from 'lib/app-context';
import * as RoutingStyle from 'lib/routing-style';

import {
  PAGEVIEW_EVENT,
  LOCATION_DETAILS_IMPRESSION,
  PRICE_IMPRESSION,
  ACCOUNT_IMPRESSION,
  COMPLETE_PURCHASE_CLICK,
  BUTTON_IMPRESSION,
  BUTTON_CLICK,
} from 'lib/analytics/events';

import getPaymentMethods from 'action-creators/payment-methods/get-payment-methods';
import getPricing from 'action-creators/checkout/get-pricing';
import getPreview from 'action-creators/checkout/get-preview';
import setDeviceData from 'action-creators/checkout/set-device-data';
import cleanupCheckoutState from 'action-creators/checkout/cleanup-checkout-state';
import trackEventCreator from 'action-creators/analytics/track-event';
import initializeCheckoutCreator from 'action-creators/checkout/initialize-checkout';
import modifyRequiredFieldListCreator from 'action-creators/checkout/modify-required-field-list';
import submitCheckout from 'action-creators/checkout/submit-checkout';
import unsetFormSubmitted from 'action-creators/checkout/unset-form-submitted';
import changeAddOns from 'action-creators/checkout/change-add-ons';
import setShouldScrollToError from 'action-creators/payment-methods/set-should-scroll-to-error';
import setError from 'action-creators/checkout/set-error';
import clearErrorCreator from 'action-creators/checkout/clear-error';
import changeUserInfo from 'action-creators/account/change-user-info';
import setModalState from 'action-creators/modal/set-modal-state';
import revertSearchCreator from 'action-creators/search/revert-search';
import handlePaymentError from 'action-creators/payment-methods/handle-payment-error';
import addPrepurchaseLicensePlateCreator from 'action-creators/checkout/add-prepurchase-license-plate';

const propTypes = {
  user: PropTypes.instanceOf(User),
  currentSearch: PropTypes.instanceOf(Search),
  pricing: PropTypes.instanceOf(Pricing),
  couponCode: PropTypes.string,
  requiredFieldErrors: PropTypes.oneOfType([
    PropTypes.instanceOf(Map),
    PropTypes.instanceOf(OrderedMap),
  ]),
  checkoutTotal: PropTypes.number,
  selectedPaymentMethod: PropTypes.instanceOf(PaymentMethod),
  error: PropTypes.instanceOf(Map),
  requiredAddOnsSelected: PropTypes.bool,
  locations: PropTypes.instanceOf(OrderedMap).isRequired,
  userCreditAmount: PropTypes.number,
  selectedAddOns: PropTypes.instanceOf(AddOnsModel),
  timezone: PropTypes.string,
  shouldScrollToError: PropTypes.bool,
  requiredFields: PropTypes.instanceOf(Set),
  firstname: PropTypes.string,
  lastname: PropTypes.string,
  email: PropTypes.string,
  venue: PropTypes.instanceOf(Venue),
  hub: PropTypes.instanceOf(Hub),
  event: PropTypes.instanceOf(Event),
  braintreeToken: PropTypes.string,
  deepLinkDisplay: PropTypes.bool,
  paymentMethods: PropTypes.instanceOf(OrderedMap),
  requestQueueLength: PropTypes.number.isRequired,
  appContext: PropTypes.string.isRequired,
  selectedLocation: PropTypes.instanceOf(Location),
  selectedQuote: PropTypes.instanceOf(Quote),
  submitted: PropTypes.bool,
  routingStyle: PropTypes.string,
  locale: PropTypes.string.isRequired,

  getPricing: PropTypes.func.isRequired,
  cleanupCheckoutState: PropTypes.func.isRequired,
  unsetFormSubmitted: PropTypes.func.isRequired,
  getPreview: PropTypes.func.isRequired,
  submitCheckout: PropTypes.func.isRequired,
  changeUserInfo: PropTypes.func.isRequired,
  setError: PropTypes.func.isRequired,
  modifyRequiredFieldList: PropTypes.func.isRequired,
  setShouldScrollToError: PropTypes.func.isRequired,
  changeModalState: PropTypes.func.isRequired,
  setDeviceData: PropTypes.func.isRequired,
  trackEvent: PropTypes.func.isRequired,
  clearError: PropTypes.func.isRequired,
  changeAddOns: PropTypes.func.isRequired,
  revertSearch: PropTypes.func.isRequired,
  handlePaymentError: PropTypes.func.isRequired,
  handleBraintreePaymentMethodError: PropTypes.func,
  sellers: PropTypes.instanceOf(Map).isRequired,
  routerLocation: routerLocationShape.isRequired,
  vehicles: PropTypes.instanceOf(OrderedMap),
  addPrepurchaseLicensePlate: PropTypes.func.isRequired,

  apiError: PropTypes.instanceOf(Map),
};

const defaultProps = {
  user: new User(),
  currentSearch: new Search(),
  pricing: new Pricing(),
  couponCode: null,
  formSubmitted: false,
  checkoutTotal: null,
  selectedPaymentMethod: null,
  error: Map(),
  requiredAddOnsSelected: false,
  userCreditAmount: null,
  shouldScrollToError: false,
  requiredFields: Set(),
  firstname: null,
  lastname: null,
  email: null,
  venue: null,
  hub: null,
  event: null,
  braintreeToken: null,
  selectedLocation: null,
  selectedQuote: null,
  selectedAddOns: new AddOnsModel(),
  deepLinkDisplay: false,
  paymentMethods: OrderedMap(),
  requiredFieldErrors: Map(),
  submitted: false,
  routingStyle: RoutingStyle.PARKWHIZ,
  timezone: moment.tz.guess(),
  handleBraintreePaymentMethodError: () => {},
  vehicles: new OrderedMap(),
  apiError: null,
};

const ERROR_BUTTON_TIMEOUT = 900;

class Checkout extends Component {
  constructor(props, context) {
    super(props);

    this._formId = 'reserveform';

    this.onUserInfoChange = this.onUserInfoChange.bind(this);
    this._clearErrorButton = this._clearErrorButton.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.showSelectedLocation = this.showSelectedLocation.bind(this);
    this.isSubmitted = this.isSubmitted.bind(this);
    this.initializeBraintree = this.initializeBraintree.bind(this);
    this.setUserInfo = this.setUserInfo.bind(this);
    this.saveSearch = this.saveSearch.bind(this);

    this.onLocationDetailsVisibilityChange = this.onLocationDetailsVisibilityChange.bind(this);
    this.onPriceVisibilityChange = this.onPriceVisibilityChange.bind(this);
    this.onAccountVisibilityChange = this.onAccountVisibilityChange.bind(this);
    this.handleMoreAboutVisibilityChange = this.handleMoreAboutVisibilityChange.bind(this);

    const { email = '', firstname = '', lastname = '', phone = '' } = this.props.user;
    this.state = {
      errorButton: false,
      pricing: new Pricing(),
      userInfo: Map({
        firstname,
        lastname,
        email,
        phone,
      }),
      savedSearch: props.currentSearch,
    };
    this.context = context;
  }

  componentDidMount() {
    import('braintree-web').then((braintree) => {
      this.initializeBraintree(braintree);
    }).catch((e) => { console.error(`Failed to initialize braintree: ${e}`); });

    const { currentSearch, trackEvent, selectedLocation, selectedQuote, event, venue, user } = this.props;
    const pageProperties = pageProps({ app: 'Checkout', currentSearch });
    const QuoteID = get(selectedQuote, 'id', currentSearch.selectedQuoteId);
    const LocationID = get(selectedLocation, 'id', currentSearch.selectedLocationId);

    trackEvent({
      ...PAGEVIEW_EVENT,
      ...pageProperties,
      name: pageProperties.pageName,
      properties: {
        ...urlProps(),
        QuoteID,
        LocationID,
        event_id: get(event, 'id', null),
        venue_id: get(venue, 'id', null),
        logged_in: user.isLoggedIn(),
      },
    });
  }

  componentWillReceiveProps(nextProps) {
    const { selectedQuote } = nextProps;
    if (selectedQuote && selectedQuote.pricings && nextProps.pricing.id === selectedQuote.getIn(['pricings', 0, 'id'])) {
      this.setState({ pricing: nextProps.pricing });
    }

    const { error, requiredFieldErrors, submitted } = nextProps;
    if (submitted && (
      (error.constructor.name === 'Map' && error.size) ||
      (error.constructor.name === 'Object' && !isEmpty) ||
      (requiredFieldErrors && requiredFieldErrors.filter(e => e).size)
    )) {
      this.setState({ errorButton: true });

      // Make form submittable again:
      // This is super hacky and brittle and if all of the checks above
      // don't pass, the form is unable to submit again.  Unfortunately,
      // components don't receive props updates until cerebral finishes
      // the signal chain, which is why we need to put this here.  Huge
      // TODO to fix later if someone has a better idea.
      this.props.unsetFormSubmitted();

      // Set timeout to remove error button after animation.
      setTimeout(this._clearErrorButton, ERROR_BUTTON_TIMEOUT);
    }
  }

  componentDidUpdate(prevProps) {
    const { clientInstance } = this.state;

    if (!clientInstance && this.props.braintreeToken) {
      import('braintree-web').then((braintree) => {
        this.initializeBraintree(braintree);
      }).catch((e) => { console.error(`Failed to initialize braintree: ${e}`); });
    }
    if (this.props.currentSearch.equals(prevProps.currentSearch) &&
        this.props.pricing.equals(prevProps.pricing) &&
        this.props.user.isLoggedIn() === prevProps.user.isLoggedIn()) { return; }

    // Refresh preview
    this.getPreview(this.props.couponCode, true);
    this.props.getPricing();
  }

  componentWillUnmount() {
    if (this.state.dataCollector) {
      this.state.dataCollector.teardown();
    }

    // Include all args that actions need to cleanup checkout
    this.props.cleanupCheckoutState({ deviceData: null });
  }

  onUserInfoChange(fields) {
    this.props.changeUserInfo(fields);
  }

  getPreview(couponCode, autoApplyCoupon) {
    this.props.getPreview({ couponCode, autoApplyCoupon });
  }

  setUserInfo(userInfo) {
    this.setState({ userInfo: Map(userInfo) });
    this.onUserInfoChange(userInfo);
  }

  saveSearch() {
    this.setState({ savedSearch: this.props.currentSearch });
  }

  initializeBraintree(braintree) {
    const { braintreeClientInstance } = this.state;
    const { braintreeToken, handleBraintreePaymentMethodError } = this.props;
    const { ApplePaySession } = window;
    if (!braintreeToken || braintreeClientInstance) { return; }

    braintree.client.create({
      authorization: braintreeToken,
    }, (clientErr, clientInstance) => {
      if (clientErr) {
        // TODO this might have to go in callback for subclient
        const fieldErrors = get(clientErr, ['details', 'originalError', 'fieldErrors', 0, 'fieldErrors']);
        this.props.handlePaymentError(fieldErrors);
        handleBraintreePaymentMethodError();
        return;
      }
      this.setState({ braintreeClientInstance: clientInstance });

      braintree.dataCollector.create({
        client: clientInstance,
        kount: true,
      }, (dataErr, dataCollector) => {
        if (dataErr) {
          // handleBraintreePaymentMethodError({ shouldScrollToError: true });
          return;
        }

        this.setState({ dataCollector });
        this.props.setDeviceData({ deviceData: dataCollector.deviceData });
      });

      if (ApplePaySession && ApplePaySession.canMakePayments()) {
        braintree.applePay.create({
          client: clientInstance,
        }, (applePayErr, applePayInstance) => {
          if (applePayErr) {
            console.error('Error creating applePayInstance:', applePayErr);
            return;
          }

          const promise = ApplePaySession.canMakePaymentsWithActiveCard(applePayInstance.merchantIdentifier);
          promise.then((canMakePaymentsWithActiveCard) => {
            if (canMakePaymentsWithActiveCard) {
              this.setState({ applePayInstance });
            }
          });
        });
      }

      braintree.paypal.create({
        client: clientInstance,
      }, (paypalFieldsErr, paypalInstance) => {
        if (paypalFieldsErr) {
          console.warn(paypalFieldsErr);
        }
        this.setState({ paypalInstance });
        // paypalButton.removeAttribute('disabled');
        // When the button is clicked, attempt to tokenize.
      });
    });
  }

  handleSubmit(e) {
    e.preventDefault();
    e.stopPropagation();

    const { selectedPaymentMethod, checkoutTotal, trackEvent } = this.props;
    const { deviceData } = this.state.dataCollector || {};
    trackEvent(COMPLETE_PURCHASE_CLICK);
    if (get(selectedPaymentMethod, 'id', false)) {
      const { value: expenseMemo } = this.checkoutForm.elements.expenseMemo || {};
      const savedPaymentToken = selectedPaymentMethod.id;

      this.props.submitCheckout({ savedPaymentToken, expenseMemo, deviceData });
    } else if (parseFloat(checkoutTotal) === 0) {
      this.props.submitCheckout({ deviceData });
    }
  }

  isSubmitted() {
    const { requiredFieldErrors, submitted } = this.props;
    return submitted && !requiredFieldErrors.size;
  }

  _clearErrorButton() {
    this.setState({ errorButton: false });
  }

  handleMoreAboutVisibilityChange(visible) {
    const { appContext, trackEvent } = this.props;
    if (visible && appContext !== AppContext.DESKTOP) {
      trackEvent({
        ...BUTTON_IMPRESSION,
        name: 'MoreAboutFacility',
        position: '0',
      });
    }
  }

  showSelectedLocation() {
    const { selectedLocation } = this.props;

    this.props.changeModalState({
      displayModal: true,
      header: 'generalHeader',
      body: 'locationDetail',
      footer: 'generalFooter',
      headerProps: {
        headerText: 'Your parking location',
      },
      bodyProps: {
        key: 'selected-location',
        location: selectedLocation,
        shouldRenderBasicInfo: false,
        imageDisplayType: 'singleImage',
      },
    });

    this.props.trackEvent({
      ...BUTTON_CLICK,
      name: 'MoreAboutFacility',
      position: '0',
    });
  }

  onLocationDetailsVisibilityChange(isVisible) {
    const { currentSearch, trackEvent, appContext } = this.props;
    if (isVisible && appContext === AppContext.DESKTOP) {
      trackEvent({
        ...LOCATION_DETAILS_IMPRESSION,
        properties: {
          LocationID: currentSearch.selectedLocationId,
        },
      });
    }
  }

  onPriceVisibilityChange(isVisible) {
    if (isVisible) {
      this.props.trackEvent({
        ...PRICE_IMPRESSION,
        properties: {
          Price: this.props.checkoutTotal,
        },
      });
    }
  }

  onAccountVisibilityChange(isVisible) {
    if (isVisible) {
      this.props.trackEvent(ACCOUNT_IMPRESSION);
    }
  }

  render() {
    const {
      selectedLocation,
      selectedQuote,
      currentSearch,
      checkoutTotal,
      deepLinkDisplay,
      error,
      clearError,
      locations,
      revertSearch,
      routingStyle,
      requestQueueLength,
    } = this.props;

    if (!selectedLocation || !selectedQuote) { return null; }

    return (
      <div id="checkout" className="container checkout">
        <ModalNotices
          quote={selectedQuote}
          currentSearch={currentSearch}
          locations={locations}
          pricing={this.state.pricing}
          deepLinkDisplay={deepLinkDisplay}
          error={error}
          clearError={clearError}
          revertSearch={revertSearch}
          savedSearch={this.state.savedSearch}
          requestQueueLength={requestQueueLength}
          routingStyle={routingStyle}
        />
        <div className="col-md-8 col-xs-12">
          <form ref={(f) => { this.checkoutForm = f; }} id={this._formId} onSubmit={this.handleSubmit}>
            <fieldset id="reserve-container">
              <div>
                <Headline
                  currentSearch={currentSearch}
                  event={this.props.event}
                />
                <SubHeadline
                  selectedQuote={selectedQuote}
                  selectedLocation={selectedLocation}
                  venue={this.props.venue}
                  event={this.props.event}
                />
                <VenueSubheader venue={this.props.venue} />
              </div>
              <div className="visible-xs">
                <MobileHeader
                  selectedLocation={selectedLocation}
                  venue={this.props.venue}
                  event={this.props.event}
                  handleMoreAboutVisibilityChange={this.handleMoreAboutVisibilityChange}
                  showSelectedLocation={this.showSelectedLocation}
                  sellers={this.props.sellers}
                  totalPrice={checkoutTotal}
                />
              </div>
              <div className="hidden-xs">
                <EventTooltip
                  sellers={this.props.sellers}
                  selectedLocation={selectedLocation}
                  event={this.props.event}
                />
              </div>
              <CheckoutForm
                {...this.props}
                errorButton={this.state.errorButton}
                trackEvent={this.props.trackEvent}
                onLocationDetailsVisibilityChange={this.onLocationDetailsVisibilityChange}
                saveSearch={this.saveSearch}
                setUserInfo={this.setUserInfo}
                userInfo={this.state.userInfo}
                applePayInstance={this.state.applePayInstance}
                dataCollector={this.state.dataCollector}
                braintreeClientInstance={this.state.braintreeClientInstance}
                paypalInstance={this.state.paypalInstance}
                isSubmitted={this.isSubmitted()}
                handleSubmit={this.handleSubmit}
                onPriceVisibilityChange={this.onPriceVisibilityChange}
                onUserInfoChange={this.onUserInfoChange}
                onAccountVisibilityChange={this.onAccountVisibilityChange}
                vehicles={this.props.vehicles}
                addPrepurchaseLicensePlate={this.props.addPrepurchaseLicensePlate}
                selectedQuote={this.props.selectedQuote}
                modifyRequiredFieldList={this.props.modifyRequiredFieldList}
                selectedLocation={this.props.selectedLocation}
                requiredFieldErrors={this.props.requiredFieldErrors}
                requiredFields={this.props.requiredFields}
              />
            </fieldset>
          </form>
        </div>
        <div className="col-md-4 col-gutter-right-0 hidden-xs hidden-sm">
          <VisibilitySensor onChange={this.onLocationDetailsVisibilityChange} partialVisibility>
            <AdditionalInformation
              selectedLocation={selectedLocation}
              quote={selectedQuote}
              checkoutTotal={checkoutTotal}
            />
          </VisibilitySensor>
        </div>
      </div>
    );
  }
}

Checkout.propTypes = propTypes;
Checkout.defaultProps = defaultProps;
Checkout.contextTypes = {
  router: PropTypes.object.isRequired,
};

const mapStateToProps = (state, ownProps) => {
  const {
    user,
    vehicles,
  } = state.account;

  const {
    currentSearch,
    previousSearch,
    locations,
    selectedLocation,
    selectedQuote,
    venue,
    event,
    timezone,
    sellers,
  } = state.search;

  const {
    checkoutTotal,
    userCreditAmount,
    couponCode,
    selectedAddOns,
    pricing,
    requiredFields: checkoutRequiredFields,
    requiredFieldErrors: checkoutRequiredFieldErrors,
    shouldScrollToError,
    submitted,
    error,
    apiError,
  } = state.checkout;

  const {
    paymentMethods,
    selectedPaymentMethod,
    braintreeToken,
    requiredFields: paymentRequiredFields,
    requiredFieldErrors: paymentRequiredFieldErrors,
  } = state.paymentMethods;

  const {
    name: app,
    appContext,
    locale,
    translations,
  } = state.app;

  const {
    display: deepLinkDisplay,
  } = state.deepLinks;

  const {
    requestQueue,
    requestQueueLength,
  } = state.requests;

  const { brand } = state.brand;

  const requiredFieldErrors = checkoutRequiredFieldErrors.merge(paymentRequiredFieldErrors);
  const requiredFields = checkoutRequiredFields.merge(paymentRequiredFields);

  let { location: routerLocation } = state.router;
  if (!routerLocation) {
    ({ routerLocation } = ownProps);
  }

  return {
    user,
    selectedLocation,
    selectedQuote,
    currentSearch,
    previousSearch,
    locations,
    venue,
    event,
    timezone,
    paymentMethods,
    selectedPaymentMethod,
    checkoutTotal,
    userCreditAmount,
    couponCode,
    selectedAddOns,
    pricing,
    requiredFields,
    requiredFieldErrors,
    shouldScrollToError,
    submitted,
    requestQueue,
    requestQueueLength,
    braintreeToken,
    app,
    appContext,
    deepLinkDisplay,
    error,
    apiError,
    locale,
    translations,
    routingStyle: brand.routingStyle,
    sellers,
    routerLocation,
    vehicles,
  };
};

const mapDispatchToProps = dispatch => (
  bindActionCreators({
    initializeCheckout: initializeCheckoutCreator,
    cleanupCheckoutState,
    getPaymentMethods,
    getPricing,
    setDeviceData,
    unsetFormSubmitted,
    getPreview,
    submitCheckout,
    changeAddOns,
    changeUserInfo,
    setError,
    modifyRequiredFieldList: modifyRequiredFieldListCreator,
    setShouldScrollToError,
    changeModalState: setModalState,
    trackEvent: trackEventCreator,
    clearError: clearErrorCreator,
    revertSearch: revertSearchCreator,
    handlePaymentError,
    addPrepurchaseLicensePlate: addPrepurchaseLicensePlateCreator,
  }, dispatch)
);

export default connect(mapStateToProps, mapDispatchToProps)(Checkout);
